import { Controller } from "@hotwired/stimulus"

// Interactive legend for the "Allowed by Repricing Type" reporting card
// (app/views/partners/reporting/_repricing_card.html.erb): clicking (or
// Enter/Space-ing) a legend item toggles that repricing type in/out of the
// card's totals. Every other stat on the card is derived client-side from
// the same per-type numbers the server already rendered — nothing is
// re-fetched — so toggling stays instant and works even if a background
// Turbo Stream update is in flight for a different card.
//
// Deliberately in scope: the four header KPIs (Total Billed, Total Allowed,
// Repricing Savings $/%, % of Medicare), the stacked bar's segment widths/
// rounding/visibility, and each legend item's own "% of total" figure.
// Deliberately out of scope: the "Top Networks"/"Top Strategies" section
// (see the muted caption rendered next to its heading). That breakdown is
// computed by a separate query (Dashboard::RepricingQuery#top_networks /
// #top_strategies) that isn't decomposed by repricing type, so there is no
// correct way to filter it by the legend selection without a new backend
// aggregation; it always reflects the full, unfiltered claim set.
export default class extends Controller {
  static targets = [
    "segment",
    "legendItem",
    "legendPct",
    "statBilled",
    "statAllowed",
    "statSavings",
    "statSavingsPct",
    "statMedicareWrap",
    "statMedicarePct",
    "legendTooltip",
    "legendTooltipTitle",
    "legendTooltipBody",
  ]

  static values = { types: Array }

  connect() {
    // Hoist the parsed JSON once: this.typesValue re-parses the whole
    // data-* attribute string on every access, and render() reads it many
    // times per toggle (once per segment/legend item), so caching it avoids
    // repeated JSON.parse work on every interaction.
    this.types = this.typesValue
    this.selected = new Set(this.types.map((t) => t.name))
    this.reprocessScroll = this.hideLegendTooltip.bind(this)
    // Any scroll (page or the legend row's own overflow-x-auto scroller)
    // moves the hovered item relative to the fixed-position tooltip without
    // firing mouseleave, so the tooltip would otherwise float in a stale
    // spot. Hiding on scroll is simpler and safer than re-tracking position
    // continuously, and matches how native title tooltips also disappear on
    // scroll.
    window.addEventListener("scroll", this.reprocessScroll, { capture: true, passive: true })
    this.cleanUpForCache = this.hideLegendTooltip.bind(this)
    document.addEventListener("turbo:before-cache", this.cleanUpForCache)
    this.render()
  }

  disconnect() {
    window.removeEventListener("scroll", this.reprocessScroll, { capture: true })
    document.removeEventListener("turbo:before-cache", this.cleanUpForCache)
    this.hideLegendTooltip()
  }

  toggle(event) {
    const name = event.currentTarget.dataset.name
    if (this.selected.has(name)) {
      // Keep at least one type selected; an all-off chart/KPI set has no
      // sensible reading and would just show a blank bar. Give visible
      // feedback that the click landed but was refused, rather than a
      // silent no-op that reads as a broken button.
      if (this.selected.size === 1) {
        this.flashRefused(event.currentTarget)
        return
      }
      this.selected.delete(name)
    } else {
      this.selected.add(name)
    }
    this.render()
  }

  flashRefused(button) {
    // Give visible feedback that the click landed but was refused, using
    // only existing Tailwind utilities (no new keyframe/animation config) —
    // a brief ring flash plus a title explaining why.
    button.setAttribute("title", "At least one repricing type must stay selected")
    button.classList.add("ring-2", "ring-alert-red-400", "rounded-md")
    window.setTimeout(() => {
      button.classList.remove("ring-2", "ring-alert-red-400", "rounded-md")
      button.removeAttribute("title")
    }, 400)
  }

  // Shared floating tooltip for legend items (see the ERB comment above the
  // markup): position:fixed, computed from the hovered/focused button's own
  // bounding rect, so it always escapes the legend row's overflow-x-auto
  // clipping regardless of scroll position — a per-item absolutely
  // positioned tooltip nested inside that scroller would be clipped instead.
  showLegendTooltip(event) {
    if (!this.hasLegendTooltipTarget) return
    const button = event.currentTarget
    const tooltip = this.legendTooltipTarget
    if (this.hasLegendTooltipTitleTarget) this.legendTooltipTitleTarget.textContent = button.dataset.tooltipTitle || ""
    if (this.hasLegendTooltipBodyTarget) this.legendTooltipBodyTarget.textContent = button.dataset.tooltipBody || ""
    button.setAttribute("aria-describedby", tooltip.id)

    // Reset position before measuring: as a `position: fixed` box with no
    // explicit width, the tooltip's shrink-to-fit width/height are
    // constrained by how much room is left between its current `left` and
    // the viewport edge. Leaving a previous hover's `left` in place would
    // mismeasure getBoundingClientRect() on every hover after the first.
    tooltip.style.left = "0px"
    tooltip.style.top = "0px"
    tooltip.classList.remove("hidden")
    const buttonRect = button.getBoundingClientRect()
    const tooltipRect = tooltip.getBoundingClientRect()
    const left = Math.max(4, Math.min(
      buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2,
      window.innerWidth - tooltipRect.width - 4
    ))
    const above = buttonRect.top - tooltipRect.height - 6
    // Flip below the button when there isn't room above (e.g. the row sits
    // near the top of the viewport) instead of clamping to y=4 and covering
    // the very row the tooltip is describing.
    const top = above >= 4 ? above : buttonRect.bottom + 6
    tooltip.style.left = `${left}px`
    tooltip.style.top = `${top}px`
  }

  hideLegendTooltip() {
    if (!this.hasLegendTooltipTarget) return
    this.legendTooltipTarget.classList.add("hidden")
    this.legendItemTargets.forEach((item) => item.removeAttribute("aria-describedby"))
  }

  render() {
    const selectedTypes = this.types.filter((t) => this.selected.has(t.name))

    const totalBilled = selectedTypes.reduce((sum, t) => sum + t.billed, 0)
    const totalAllowed = selectedTypes.reduce((sum, t) => sum + t.allowed, 0)
    const totalMedicare = selectedTypes.reduce((sum, t) => sum + (t.medicare || 0), 0)
    const totalSavings = totalBilled - totalAllowed
    const savingsPct = totalBilled > 0 ? round(((totalBilled - totalAllowed) / totalBilled) * 100, 1) : 0
    const pctOfMedicare = totalMedicare > 0 ? round((totalAllowed / totalMedicare) * 100, 0) : null

    if (this.hasStatBilledTarget) {
      this.statBilledTarget.textContent = abbreviatedCurrency(totalBilled)
      this.statBilledTarget.title = exactCurrency(totalBilled)
    }
    if (this.hasStatAllowedTarget) {
      this.statAllowedTarget.textContent = abbreviatedCurrency(totalAllowed)
      this.statAllowedTarget.title = exactCurrency(totalAllowed)
    }
    if (this.hasStatSavingsTarget) {
      this.statSavingsTarget.textContent = abbreviatedCurrency(totalSavings)
      this.statSavingsTarget.title = exactCurrency(totalSavings)
    }
    if (this.hasStatSavingsPctTarget) this.statSavingsPctTarget.textContent = `${savingsPct.toFixed(1)}%`
    if (this.hasStatMedicareWrapTarget) {
      this.statMedicareWrapTarget.classList.toggle("hidden", pctOfMedicare === null)
      if (pctOfMedicare !== null && this.hasStatMedicarePctTarget) {
        this.statMedicarePctTarget.textContent = `${pctOfMedicare}%`
      }
    }

    // Bar: hide deselected segments, resize the rest proportionally to the
    // selected allowed total, and keep rounded corners on the first/last
    // still-visible segment (which type is first/last visible can change
    // as items are toggled).
    const visibleSegments = []
    this.segmentTargets.forEach((segment) => {
      const name = segment.dataset.name
      const isSelected = this.selected.has(name)
      segment.classList.toggle("hidden", !isSelected)
      if (isSelected) {
        const type = this.types.find((t) => t.name === name)
        const pct = totalAllowed > 0 ? (type.allowed / totalAllowed) * 100 : 0
        segment.style.width = `${pct}%`
        visibleSegments.push(segment)
      }
    })
    visibleSegments.forEach((segment, i) => {
      segment.classList.toggle("rounded-l-lg", i === 0)
      segment.classList.toggle("rounded-r-lg", i === visibleSegments.length - 1)
      segment.style.borderRight = i < visibleSegments.length - 1 ? "2px solid white" : ""
    })

    // Legend: dim deselected items, recompute each SELECTED item's "% of
    // total" against the selected allowed total (so the visible percentages
    // always sum to ~100% of what's currently shown). A deselected item's %
    // is blanked (not recomputed and not left showing a stale number): its
    // old value was relative to whatever was selected the moment it was
    // itself last selected, which can differ from the current selection in
    // an order-dependent way after multiple toggles, so any number we could
    // show without recomputing per-type sums server-side would be
    // misleading. Blanking makes "excluded from the totals above" explicit.
    this.legendItemTargets.forEach((item) => {
      const name = item.dataset.name
      const isSelected = this.selected.has(name)
      item.classList.toggle("opacity-40", !isSelected)
      item.setAttribute("aria-pressed", isSelected ? "true" : "false")
    })
    this.legendPctTargets.forEach((pctEl) => {
      const name = pctEl.dataset.name
      if (!this.selected.has(name)) {
        pctEl.textContent = "—"
        return
      }
      const type = this.types.find((t) => t.name === name)
      if (!type) return
      const pct = totalAllowed > 0 ? Math.round((type.allowed / totalAllowed) * 100) : 0
      pctEl.textContent = `${pct}%`
    })
  }
}

function round(value, precision) {
  const factor = 10 ** precision
  return Math.round(value * factor) / factor
}

// Mirrors Rails' number_to_currency(amount, precision: 2), used to keep the
// KPI `title` tooltips showing the exact selected-total value (not the
// abbreviated display string) after a legend toggle changes the total.
function exactCurrency(amount) {
  const sign = amount < 0 ? "-" : ""
  const formatted = Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${sign}$${formatted}`
}

// Mirrors Partners::DashboardHelper#abbreviated_currency so client-side
// recomputed totals render identically to the server-rendered initial
// values ($28.4M, $1.2K, etc.).
function abbreviatedCurrency(amount) {
  const abs = Math.abs(amount)
  const sign = amount < 0 ? "-" : ""

  if (abs >= 1_000_000_000) return `${sign}$${(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`

  return `${sign}$${Math.round(abs).toLocaleString("en-US")}`
}
